import {
  OWN_COPY_STATES,
  type AiCopyFilter,
  type AiCopyItem,
  type AiCopySummary,
  type ApproveAiCopyResponse,
  type ContentState,
} from '@avrahami/contracts';
import { countText, fill } from '@avrahami/ui';
import { he } from '../i18n/he';
import type { CopyField, CopyRowState } from './copy-types';

/**
 * The screen of one store's marketing copy (docs/AI_CONTENT.md, adr/0023): what its numbers, rows
 * and requests mean. The screen itself only presents.
 */

/** How often copy that is being written is read again. */
export const AI_COPY_POLL_MS = 4_000;

/** Copy is being written (or waits for its turn): the screen follows it until it ends. */
export function copyWorkOpen(summary: AiCopySummary): boolean {
  // Stopped writing only finishes what is being written now: the queue does not move.
  if (summary.stopped)
    return summary.jobs.running > 0 || summary.storeSeo.job?.status === 'RUNNING';
  return summary.jobs.queued + summary.jobs.running > 0 || jobOpen(summary.storeSeo);
}

/** How many catalog products have copy of the store's own, out of all of them. */
export function copyProgress(summary: AiCopySummary): {
  ready: number;
  total: number;
  percent: number;
} {
  const { total, withoutCopy } = summary.products;
  const ready = Math.max(0, total - withoutCopy);
  return { ready, total, percent: total === 0 ? 0 : Math.floor((ready / total) * 100) };
}

/** The number on a filter's chip: how many products its list holds. */
export function filterCount(summary: AiCopySummary, filter: AiCopyFilter): number {
  switch (filter) {
    case 'GENERATED':
      return summary.products.generated;
    case 'IN_PROGRESS':
      return summary.jobs.queued + summary.jobs.running;
    case 'FAILED':
      return summary.jobs.failed;
    case 'READY':
      return summary.products.approved + summary.products.edited;
    case 'WITHOUT_COPY':
      return summary.products.unqueued;
  }
}

const OWN_COPY: ReadonlySet<ContentState> = new Set(OWN_COPY_STATES);

/** A job of the product is queued or running: its copy is about to change. */
/** A job as the texts about it need it: of a product's copy, or of the store's search texts. */
interface WithJob {
  job: Pick<NonNullable<AiCopyItem['job']>, 'status' | 'errorCode'> | null;
}

export function jobOpen(item: WithJob): boolean {
  return item.job?.status === 'QUEUED' || item.job?.status === 'RUNNING';
}

export function hasOwnCopy(item: Pick<AiCopyItem, 'contentState'>): boolean {
  return OWN_COPY.has(item.contentState);
}

/** Only copy the engine wrote, and nobody approved or edited yet, can be approved. */
export function canApprove(item: Pick<AiCopyItem, 'contentState' | 'job'>): boolean {
  return item.contentState === 'AI_GENERATED' && !jobOpen(item);
}

export function copyRowState(item: Pick<AiCopyItem, 'contentState' | 'job'>): CopyRowState {
  if (jobOpen(item)) return 'IN_PROGRESS';
  switch (item.contentState) {
    case 'AI_GENERATED':
      return 'GENERATED';
    case 'APPROVED':
      return 'APPROVED';
    case 'TENANT_EDITED':
      return 'EDITED';
    case 'SOURCE_PENDING':
      return item.job?.status === 'FAILED' ? 'FAILED' : 'WITHOUT_COPY';
  }
}

/** Why the product's last job failed, in words; null when it did not fail. */
export function jobErrorText(item: WithJob): string | null {
  if (item.job?.status !== 'FAILED') return null;
  const reason =
    item.job.errorCode === null
      ? he.aiCopy.jobErrorUnknown
      : he.aiCopy.jobErrors[item.job.errorCode];
  return fill(he.aiCopy.jobFailed, { reason });
}

/** The products of a list that can be ticked for "approve the selected". */
export function approvableIds(items: readonly AiCopyItem[]): string[] {
  return items.filter(canApprove).map((item) => item.productId);
}

/** Where the screen previews an image of the store (`AiCopyItem.image`). */
export function copyImageUrl(tenantId: string, file: string): string {
  return `/v1/platform/tenants/${encodeURIComponent(tenantId)}/media/${encodeURIComponent(file)}`;
}

// ---------------------------------------------------------------------------------------------
// Editing a product's copy
// ---------------------------------------------------------------------------------------------

export { COPY_FIELDS, type CopyField, type CopyRowState } from './copy-types';

/**
 * The longest each field may be: the limits of the copy update (`updateAiCopyRequestSchema`),
 * which does not export them. A test checks that they are the same.
 */
export const COPY_LIMITS = {
  title: 200,
  description: 20_000,
  seoTitle: 70,
  seoDescription: 320,
} as const satisfies Record<CopyField, number>;

export type CopyDraft = Record<CopyField, string>;

/** "12 of 70 characters", as the field's hint. */
export function copyCounter(draft: CopyDraft, field: CopyField): string {
  return fill(he.aiCopy.counter, { count: draft[field].trim().length, max: COPY_LIMITS[field] });
}

// ---------------------------------------------------------------------------------------------
// Words
// ---------------------------------------------------------------------------------------------

const LONG_TEXT_CHARS = 240;
const LONG_TEXT_LINES = 3;

/** A description too long for its row: shown cut, with "show more". */
export function isLongText(text: string): boolean {
  return text.length > LONG_TEXT_CHARS || text.split('\n').length > LONG_TEXT_LINES;
}

/** What an approval did: how many were approved, and how many of them shoppers now see. */
export function approvalText(result: ApproveAiCopyResponse): string {
  if (result.approved === 0) return he.aiCopy.approvedNone;
  const approved = countText(result.approved, {
    one: he.aiCopy.approvedOne,
    many: he.aiCopy.approvedMany,
  });
  if (result.published === 0) return approved;
  return `${approved} ${countText(result.published, {
    one: he.aiCopy.publishedOne,
    many: he.aiCopy.publishedMany,
  })}`;
}

/** What a writing request did: how many products entered the queue. */
export function queuedText(queued: number): string {
  if (queued === 0) return he.aiCopy.queuedNone;
  return countText(queued, { one: he.aiCopy.queuedOne, many: he.aiCopy.queuedMany });
}

/** What "write all the generated copy again" is about to do: how many products, and that it costs. */
export function regenerateAllText(generated: number): string {
  const products = countText(generated, {
    one: he.aiCopy.regenerateAllOne,
    many: he.aiCopy.regenerateAllMany,
  });
  return `${products} ${he.aiCopy.regenerateAllCost}`;
}

/** An empty list, in words: nothing matches the search, or nothing is in this state. */
export function emptyText(filter: AiCopyFilter, searching: boolean): string {
  return searching ? he.aiCopy.noResults : he.aiCopy.empty[filter];
}
